import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ExternalLink, Lock, Mail, Sparkles, Timer } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PRO_ACCESS_URL, PRO_BUNDLE_NAME } from '../config/proFeatures';
import UpgradeRequestCard from '../components/UpgradeRequestCard';

const UNLOCKS = [
  'Unlimited AI courses + generations',
  'AI Chart Analyzer — entry, target & stop ideas',
  'Advanced screener with 30+ filters',
  'Full strategy backtesting + marketplace',
  'Pro-only alerts, journal & research',
  'Member-only Discord + Telegram community',
];

const STEPS = [
  {
    icon: ExternalLink,
    title: `Buy ${PRO_BUNDLE_NAME}`,
    body: 'Head to our official Linktree and purchase the digital wealth-information bundle. It includes your trading-app membership.',
  },
  {
    icon: Mail,
    title: 'Use your account email',
    body: 'Purchase with the same email you signed up with here, so our team can match your order to your account.',
  },
  {
    icon: Timer,
    title: 'Unlocked within 24 hours',
    body: 'Your account is manually processed and upgraded to Pro access within 24 hours of purchase. No further action needed.',
  },
];

export default function ProAccess() {
  const { user, isPro } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#07070d] via-[#0a0a14] to-[#0d0d1f] pt-24 pb-20 text-white">
      <div className="max-w-4xl mx-auto px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.18em] text-cyan-300">
            <Sparkles className="w-3 h-3" />
            Pro Access
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Unlock{' '}
            <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
              everything
            </span>
            .
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto text-base">
            Pro access is included with{' '}
            <span className="text-white font-semibold">{PRO_BUNDLE_NAME}</span> — our digital
            product bundle of wealth information and trading-app membership, available on our
            official Linktree.
          </p>
        </div>

        {isPro && (
          <div
            className="mb-10 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5 text-center"
            data-testid="pro-access-already-pro"
          >
            <p className="text-emerald-300 font-semibold">
              You already have Pro access — everything is unlocked.
            </p>
          </div>
        )}

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6"
                data-testid={`pro-access-step-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-cyan-300" />
                  </div>
                  <span className="text-xs uppercase tracking-[0.18em] text-white/40">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold mb-1.5">{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>

        {/* CTA card */}
        <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0a0a14]/80 backdrop-blur-md p-8 md:p-10 shadow-[0_25px_80px_-30px_rgba(99,102,241,0.55)]">
          <div
            aria-hidden
            className="absolute -top-32 -right-32 w-80 h-80 rounded-full opacity-40 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(34,211,238,0.4) 0%, rgba(139,92,246,0.18) 50%, transparent 70%)',
            }}
          />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-lg font-bold mb-4">What Pro unlocks</h2>
              <ul className="space-y-2.5 text-sm">
                {UNLOCKS.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-white/80">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col items-start md:items-center gap-3">
              <a
                href={PRO_ACCESS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full md:w-auto"
              >
                <Button
                  className="w-full bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-400 hover:to-violet-400 text-white font-semibold px-8 py-6 text-base shadow-[0_0_25px_rgba(139,92,246,0.5)]"
                  data-testid="pro-access-buy-btn"
                >
                  Get {PRO_BUNDLE_NAME}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              {!user && (
                <Link to="/auth" className="w-full md:w-auto">
                  <Button
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 bg-transparent"
                    data-testid="pro-access-signin-btn"
                  >
                    <Lock className="w-3.5 h-3.5 mr-1.5" />
                    Create your free account first
                  </Button>
                </Link>
              )}
              <p className="text-[11px] text-white/40 text-left md:text-center max-w-xs">
                After purchase, your account is manually upgraded to Pro within 24 hours. Questions?
                Reach out via the contact options on our Linktree.
              </p>
            </div>
          </div>
        </div>

        {/* Already purchased? Submit order email for manual processing */}
        {user && !isPro && <UpgradeRequestCard userEmail={user.email} />}

        <p className="mt-8 text-center text-[11px] text-white/40">
          Educational platform · Not a hedge fund · Not an investment advisor · Not a broker
        </p>
      </div>
    </div>
  );
}
